import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { eventApiLogger } from "../../services/apiServices";
import { logSelfcareEventApi } from "../templates/selfcare/api/selfcareApiServices";
import {
  getQueryParam,
  getPageLoadtime,
  getOrCreateGsid,
  getVenueDetailsFromUrl,
} from "../../utils/utils";
import { getBrowserAndOS } from "../../utils/getUserAgent";
import eventBus from "../../utils/eventBus";
import {
  AUTH_EVENTS,
  NAVIGATION_EVENTS,
  API_EVENTS,
  USER_EVENTS,
  masks,
  MASK,
  ERROR_EVENTS,
  SELFCARE_EVENTS,
} from "../../utils/eventConstants";
import transformTitleTypeAndMapUrl from "../../utils/transformMapTitle";

const isSelfcarePath = window.location.hostname.includes("my");

const logEventApi = (data: object) => {
  const { browser, browserVersion, os } = getBrowserAndOS();
  const { subdomain, venueId, productType, sublocation } = getVenueDetailsFromUrl();
  const urlSearchParams = new URLSearchParams(window.location.search);

  const eventData = {
    ap_mac: getQueryParam("ap_mac"),
    gsid: getOrCreateGsid(),
    mac: getQueryParam("mac"),
    event: "onLoad",
    browser: browser,
    browser_version: browserVersion,
    event_level: "analytics",
    event_source: "frontend",
    event_type: "page_hit",
    location: window.location.href,
    query_params: {
      ...Object.fromEntries(urlSearchParams.entries()),
    },
    on_load_ms: getPageLoadtime(),
    os: os,
    page_title: transformTitleTypeAndMapUrl()?.title,
    page_name: transformTitleTypeAndMapUrl()?.type,
    product: "web-app-portal",
    referrer: document.referrer,
    screen_resolution: `${window.screen.width}x${window.screen.height}`,
    venue: venueId ?? "",
    sublocation: sublocation ?? "",
    subdomain: subdomain ?? "",
    product_type: productType ?? "",
    user_agent: navigator.userAgent,
    ...data,
  };
  if (masks) {
    const masksList = masks as string[];
    masksList.forEach((mask) => {
      if (eventData && eventData[mask as keyof typeof eventData]) {
        (eventData as any)[mask] = MASK;
      }
    });
  }

  // Determine which API to use based on the current hostname, this is on GCP (dev|qa|prod)
  //localhost will use eventApiLogger since hostname is localhost, remote GCP will use logSelfcareEventApi for only selfcare path
  if (isSelfcarePath) {
    logSelfcareEventApi({ ...eventData });
  } else {
    eventApiLogger({ ...eventData });
  }
};

// Define event handlers with their corresponding event types
const createEventHandlers = () => {
  const handlers = [
    {
      eventType: AUTH_EVENTS.DEADMAN_LOGIN,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const loginData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "deadman_auth_success",
          link_login_only: getQueryParam("link_login_only"),
          username: loginData.username,
        });
      },
    },
    {
      eventType: AUTH_EVENTS.DEADMAN_LOGIN_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "deadman_auth_error",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue_id: errorData.configsCredentials?.venue_id,
        });
      },
    },
    // Wifi Signup events
    {
      eventType: USER_EVENTS.USER_SIGNUP,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const signupData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "user_signup_success",
          link_login_only: getQueryParam("link_login_only"),
          username: signupData.username,
          password: signupData.password,
          venue: signupData.venue_id,
        });
      },
    },
    {
      eventType: USER_EVENTS.USER_SIGNUP_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "user_signup_error",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    // Wifi Generate Credentials events
    {
      eventType: USER_EVENTS.USER_GENERATE_CREDENTIALS,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const credsData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "user_generate_credentials_success",
          link_login_only: getQueryParam("link_login_only"),
          username: credsData.username,
          password: credsData.password,
          venue: credsData.venue_id,
          credentials_type: credsData.credentials_type,
        });
      },
    },
    {
      eventType: USER_EVENTS.USER_GENERATE_CREDENTIALS_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "user_generate_credentials_error",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue: errorData.venue_id,
          credentials_type: errorData.credentials_type,
        });
      },
    },

    {
      eventType: USER_EVENTS.USER_LOGIN,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const loginData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "analytics",
          event_details: "user_auth_success",
          link_login_only: getQueryParam("link_login_only"),
          username: loginData.username,
          venue: loginData.venue_id,
        });
      },
    },
    {
      eventType: USER_EVENTS.USER_LOGIN_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;

        logEventApi({
          event_type: "auth",
          event_level: "error",
          event_details: "user_auth_error",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    // Venue Error Events 
    {
      eventType: ERROR_EVENTS.FAILURE_TO_LOAD_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;
        
        logEventApi({
          event_level: "error",
          event_details: "gcs_and_fallback_load_failure",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    {
      eventType: ERROR_EVENTS.GCS_FAILURE_ERROR,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const errorData = customEvent.detail;

        logEventApi({
          event_level: "error",
          event_details: "gcs_failure_fallback_to_local_data",
          link_login_only: getQueryParam("link_login_only"),
          error_message: errorData.error,
          venue: errorData.venue_id,
        });
      },
    },
    // Selfcare Cancel Flow Events 
    {
      eventType: SELFCARE_EVENTS.CANCEL_REASON_SELECTION,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const eventData = customEvent.detail;
        
        logEventApi({
          event_type: "click",
          event_level: "analytics",
          event_details: eventData.event_details,
          code: eventData.code,
          venue: eventData.venue_id,
        });
      },
    },
    {
      eventType: SELFCARE_EVENTS.CANCEL_SUBREASON_SELECTION,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const eventData = customEvent.detail;
        
        logEventApi({
          event_type: "click",
          event_level: "analytics",
          event_details: eventData.event_details,
          venue: eventData.venue_id,
        });
      },
    },
    {
      eventType: SELFCARE_EVENTS.SAVE_OFFER_CLAIMED,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const eventData = customEvent.detail;
        
        logEventApi({
          event_type: "click",
          event_level: "analytics",
          event_details: eventData.event_details, 
          element_clicked: eventData.element_clicked,
          venue: eventData.venue_id,
          save_offer: eventData.save_offer,
        });
      },
    },
    {
      eventType: SELFCARE_EVENTS.BTN_CLICK,
      handler: (event: Event) => {
        const customEvent = event as CustomEvent;
        const eventData = customEvent.detail;
        
        logEventApi({
          event_type: "click",
          event_level: "analytics",
          event_details: eventData.event_details, 
          element_clicked: eventData.element_clicked,
          venue: eventData.venue_id,
        });
      },
    },

    // Add more event handlers here as needed
    // Example:
    // {
    //   eventType: USER_EVENTS.USER_LOGOUT,
    //   handler: (event: Event) => {
    //     const customEvent = event as CustomEvent;
    //     logEventApi({
    //       event_type: "auth",
    //       event_level: "analytics",
    //       event_details: "user_logout",
    //     });
    //   }
    // },
  ];

  return handlers;
};

const EventAPI: React.FC = () => {
  const location = useLocation();
  const previousPathRef = useRef<string | null>(null);

  // Listen for all defined events
  useEffect(() => {
    const eventHandlers = createEventHandlers();

    // Register all event handlers
    eventHandlers.forEach(({ eventType, handler }) => {
      eventBus.on(eventType, handler as EventListener);
    });

    // Clean up all event handlers
    return () => {
      eventHandlers.forEach(({ eventType, handler }) => {
        eventBus.remove(eventType, handler as EventListener);
      });
    };
  }, []);

  // Simplified route change and initial load tracking
  useEffect(() => {
    // Log initial page load if this is the first render
    if (previousPathRef.current === null) {
      logEventApi({
        product: isSelfcarePath ? "web-app-portal: selfcare" : "web-app-portal",
      });
    }
    // Log route change if path has changed
    else if (previousPathRef.current !== location.pathname) {
      logEventApi({
        event: "routeChange",
        event_type: NAVIGATION_EVENTS.ROUTE_CHANGE,
        path: location.pathname,
        previous_path: previousPathRef.current,
        product: isSelfcarePath ? "web-app-portal: selfcare" : "web-app-portal",
      });
    }

    // Update the previous path reference
    previousPathRef.current = location.pathname;
  }, [location.pathname]);

  return null;
};

export default EventAPI;
